import { flash } from "@xola/ui-kit";
import { isString } from "formik";
import { RefObject } from "react";
import { CardInputMethod, Environment, Payment, PaymentGatewayErrors, PaymentLineItemStatus, Seller } from "types";
import { PaymentInputRef, TokenizePaymentParams } from "../components/Input/PaymentInputRef";
import { SplitPayFormValues } from "../components/PurchaseCreate/SplitPayment/CreatePurchaseSplitPayTypes";
import { CollectBalanceFormValues } from "../components/PurchaseModification/CollectPurchaseBalanceTypes";
import { isAdditionalBillingInfoRequired, isAuthorizeNetEnabled } from "../models/Seller";
import { isPurchaseNotFoundError } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { CreatePurchaseState } from "@/stores/createPurchaseStore";

export const getAuthNetPurchasePayload = (
    tokenizePaymentParams: TokenizePaymentParams,
    purchase: CreatePurchaseState,
    paymentStatus?: PaymentLineItemStatus,
    cardInputMethod?: CardInputMethod,
) => {
    const { response, errorMessage, last4 } = tokenizePaymentParams;

    if (!errorMessage && response?.data?.opaqueData?.dataValue) {
        return {
            ...purchase,
            lineItems: purchase.lineItems.map((lineItem) => {
                if (lineItem.payment) {
                    let payment = {
                        ...lineItem.payment,
                        token: { id: response.data.opaqueData.dataValue },
                        last4,
                        ...(cardInputMethod ? { inputMethod: CardInputMethod.SWIPE } : null),
                    };

                    if (response.billingDetails) {
                        const { firstName, lastName, address1, city, state, country } = response.billingDetails;
                        payment = {
                            ...payment,
                            billingDetails: {
                                firstName: firstName,
                                lastName: lastName,
                                address1: address1,
                                city: city,
                                state: state,
                                country: country,
                            },
                        };
                    }

                    const paymentLineItem = {
                        ...lineItem,
                        payment: payment,
                        status: paymentStatus ?? PaymentLineItemStatus.PROCESSING,
                    };

                    return paymentLineItem;
                }

                return lineItem;
            }),
        };
    }

    throw new Error(errorMessage ?? "Could not generate token for Authorize.Net gateway");
};

export const getAuthNetLineItemPayment = (tokenizePaymentParams: TokenizePaymentParams, payment: Payment): Payment => {
    const { response, errorMessage, last4 } = tokenizePaymentParams;

    let authPayment = payment;

    if (response?.billingDetails) {
        const { firstName, lastName, address1, city, state, country } = response.billingDetails;
        authPayment = {
            ...payment,
            billingDetails: {
                firstName: firstName,
                lastName: lastName,
                address1: address1,
                city: city,
                state: state,
                country: country,
            },
        };
    }

    if (!errorMessage && response?.data?.opaqueData?.dataValue) {
        return {
            ...authPayment,
            token: { id: response.data.opaqueData.dataValue },
            last4,
        };
    }

    return authPayment;
};

export const getEnvironment = () => {
    return process.env.ENVIRONMENT === "production" ? Environment.PRODUCTION : Environment.SANDBOX;
};

export const shouldShowAdditionalBillingInfo = (status: number, seller: Seller) => {
    return status === 402 && isAuthorizeNetEnabled(seller) && !isAdditionalBillingInfoRequired(seller);
};

export const getFormValuesForAuthorizeNet = async (
    values: SplitPayFormValues | CollectBalanceFormValues,
    paymentInputRef: RefObject<PaymentInputRef>,
    tokenizePaymentParams?: TokenizePaymentParams,
    cardInputMethod?: CardInputMethod,
) => {
    if (!isString(values.payment.card)) {
        // If we already have tokenizePaymentParams, we wont tokenize card again.
        const data = tokenizePaymentParams ?? (await paymentInputRef.current?.tokenize?.());
        const payment = getAuthNetLineItemPayment(data as TokenizePaymentParams, values.payment);
        return {
            ...values,
            payment: { ...payment, ...(cardInputMethod ? { inputMethod: CardInputMethod.SWIPE } : null) },
        };
    }

    return values;
};

export const handlePaymentError = (error: any, seller: Seller, paymentInputRef: RefObject<PaymentInputRef>) => {
    const { response } = error;
    // This is required to open additional billing details modal in case there is an error while making authorize.net payment
    if (shouldShowAdditionalBillingInfo(response.status, seller)) {
        paymentInputRef.current?.onPaymentError?.(PaymentGatewayErrors.OPEN_ADDITIONAL_INFO_MODAL);
    } else if (error.response?.data.global) {
        flash.danger(error.response?.data.global);
    } else if (isPurchaseNotFoundError(error)) {
        flash.danger("Error on collecting balance, please try again");
    }
};
